import { useState } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import { adminReads } from "../api/admin-api.js";
import type { DemandReport, SettlementStatement } from "../api/types.js";
import { centiSunToSun, count, microPercent, trx, utc } from "../format.js";
import { downloadCsv, toCsv } from "../lib/csv.js";
import {
  Address,
  Blank,
  Button,
  ErrorNote,
  inputClass,
  Note,
  Panel,
  ScreenHeader,
  Spinner,
  Table,
  Td,
  Th,
} from "../ui.js";

/**
 * What the fund is shown: the demand report of b2b-pricing.md §3 and the settlement statement.
 *
 * `E` AND `k` ARE NULL WHEN `s = 1`, and the table prints that as `—` with the reason in the
 * cell's title. It is not a missing number: with every unit bought from a provider there is no
 * own capacity for `E` to be the price OF, and `k = s/(1 − s)` is unbounded. A zero there would
 * be a lie in the one figure the fund's raise is argued from.
 *
 * THE CSV CARRIES THE API'S INTEGERS, not the rendered text (`lib/csv.ts`): a spreadsheet that
 * has to reconcile against the API must hold the same numbers the API published, and the header
 * names the scale.
 */

export const DEMAND_CSV_HEADER = [
  "day",
  "day_part",
  "channel",
  "format",
  "orders",
  "energy_sold",
  "energy_ordered",
  "revenue_sun",
  "refusals",
  "p_centi_sun",
  "u_micro",
  "s_micro",
  "q_centi_sun",
  "k_micro",
  "e_centi_sun",
  "own_energy",
  "provider_energy",
  "provider_cost_sun",
  "overflow_pnl_sun",
  "stake_needed_sun",
] as const;

export function demandCsv(report: DemandReport): string {
  return toCsv(
    DEMAND_CSV_HEADER,
    report.data.map((row) => [
      row.day,
      row.day_part,
      row.channel,
      row.format,
      row.orders,
      row.energy_sold,
      row.energy_ordered,
      row.revenue_sun,
      row.refusals,
      row.p_centi_sun,
      row.u_micro,
      row.s_micro,
      row.q_centi_sun,
      row.k_micro,
      row.e_centi_sun,
      row.own_energy,
      row.provider_energy,
      row.provider_cost_sun,
      row.overflow_pnl_sun,
      row.stake_needed_sun,
    ]),
  );
}

export const SETTLEMENT_CSV_HEADER = [
  "wallet_address",
  "day",
  "sun_days",
  "energy_consumed",
  "amount_sun",
  "entries",
  "samples",
] as const;

export function settlementCsv(statement: SettlementStatement): string {
  return toCsv(
    SETTLEMENT_CSV_HEADER,
    statement.data.map((row) => [
      row.wallet_address,
      row.day,
      row.sun_days,
      row.energy_consumed,
      row.amount_sun,
      row.entries,
      row.samples,
    ]),
  );
}

export function FundReportScreen() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const period = {
    ...(from === "" ? {} : { from: `${from}T00:00:00.000Z` }),
    ...(to === "" ? {} : { to: `${to}T23:59:59.999Z` }),
  };

  const demand = useResource(
    (signal) => {
      void signal;
      return adminReads.demand(period);
    },
    [from, to],
  );
  const settlement = useResource(
    (signal) => {
      void signal;
      return adminReads.settlement(period);
    },
    [from, to],
  );

  return (
    <>
      <ScreenHeader
        title="Fund report"
        lead="Computed on every read, not from a cached job: a counterparty asking what it is owed must not get a stale answer because a scheduled job is behind. Default window is the last 30 days."
        action={
          <div class="flex items-end gap-2">
            <label class="text-[11px] text-ink-muted">
              <span class="mb-1 block">From (UTC)</span>
              <input
                class={`${inputClass} w-36`}
                type="date"
                value={from}
                onInput={(event) => setFrom((event.currentTarget as HTMLInputElement).value)}
              />
            </label>
            <label class="text-[11px] text-ink-muted">
              <span class="mb-1 block">To (UTC)</span>
              <input
                class={`${inputClass} w-36`}
                type="date"
                value={to}
                onInput={(event) => setTo((event.currentTarget as HTMLInputElement).value)}
              />
            </label>
          </div>
        }
      />

      <div class="space-y-3">
        <Panel
          title="Demand report"
          subtitle="One row per UTC day × day-part × channel. p is the price charged, u utilisation, s the provider share, q the average overflow cost, E = u × (p + k × (p − q))."
          action={
            demand.data == null ? undefined : (
              <Button
                tone="ghost"
                onClick={() =>
                  downloadCsv(
                    `demand-${demand.data!.from.slice(0, 10)}-${demand.data!.to.slice(0, 10)}.csv`,
                    demandCsv(demand.data!),
                  )
                }
              >
                Export CSV
              </Button>
            )
          }
        >
          {demand.loading ? (
            <Spinner />
          ) : demand.error !== null ? (
            <ErrorNote error={demand.error} onRetry={() => demand.reload()} />
          ) : demand.data == null ? null : (
            <>
              <Totals report={demand.data} />
              <DemandTable report={demand.data} />
              <h3 class="mt-4 mb-1 num text-[10.5px] font-medium uppercase tracking-[0.08em] text-ink-muted">
                Per class (the ask ladder)
              </h3>
              <LadderClassTable report={demand.data} />
            </>
          )}
        </Panel>

        <Panel
          title="Settlement statement"
          subtitle="Per fund wallet, per day: capacity held in SUN-days, energy actually consumed, and what the platform owes."
          action={
            settlement.data == null ? undefined : (
              <Button
                tone="ghost"
                onClick={() =>
                  downloadCsv(
                    `settlement-${settlement.data!.from}-${settlement.data!.to}.csv`,
                    settlementCsv(settlement.data!),
                  )
                }
              >
                Export CSV
              </Button>
            )
          }
        >
          {settlement.loading ? (
            <Spinner />
          ) : settlement.error !== null ? (
            <ErrorNote error={settlement.error} onRetry={() => settlement.reload()} />
          ) : settlement.data == null ? null : (
            <>
              <div class="mb-2 text-[11px] text-ink-muted">
                {settlement.data.from} → {settlement.data.to}, total{" "}
                <span class="text-ink">{trx(settlement.data.total_amount_sun)}</span>
              </div>
              <Table
                rows={settlement.data.data.length}
                empty="Nothing settled in this window."
                head={
                  <tr>
                    <Th>Wallet</Th>
                    <Th>Day</Th>
                    <Th numeric>SUN-days</Th>
                    <Th numeric>Energy consumed</Th>
                    <Th numeric>Amount</Th>
                    <Th numeric>Entries</Th>
                    <Th numeric>Samples</Th>
                  </tr>
                }
              >
                {settlement.data.data.map((row) => (
                  <tr key={`${row.wallet_address}-${row.day}`}>
                    <Td>
                      <Address value={row.wallet_address} />
                    </Td>
                    <Td>{row.day}</Td>
                    <Td numeric>{count(row.sun_days)}</Td>
                    <Td numeric>{count(row.energy_consumed)}</Td>
                    <Td numeric>{trx(row.amount_sun)}</Td>
                    <Td numeric>{count(row.entries)}</Td>
                    <Td numeric>{count(row.samples)}</Td>
                  </tr>
                ))}
              </Table>
            </>
          )}
        </Panel>
      </div>
    </>
  );
}

function Totals({ report }: { report: DemandReport }) {
  const totals = report.totals;
  const generated = (
    <div class="num text-[10.5px] text-ink-muted sm:col-span-3 lg:col-span-6">
      Generated {utc(report.generated_at)} · ratio {report.ratio_micro / 1_000_000} energy per
      staked TRX per day · scales: {report.scales.price}, {report.scales.ratios},{" "}
      {report.scales.money}
    </div>
  );
  // An empty window is said once, below, by the table — not as a row of zero cards.
  if (totals.orders === 0) return <div class="mb-3">{generated}</div>;
  return (
    <div class="mb-3 grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
      {[
        { label: "Orders", value: count(totals.orders) },
        { label: "Energy sold", value: count(totals.energy_sold) },
        { label: "Revenue", value: trx(totals.revenue_sun) },
        { label: "Overflow share", value: microPercent(totals.overflow_share_micro) },
        { label: "Overflow P&L", value: trx(totals.overflow_pnl_sun) },
        { label: "Stake needed", value: trx(totals.stake_needed_sun, 0) },
      ].map((tile) => (
        <div key={tile.label} class="rounded-[12px] border border-line bg-surface-raised px-3 py-2">
          <div class="num text-[10px] tracking-[0.08em] text-ink-muted uppercase">{tile.label}</div>
          <div class="num mt-1 text-[15px] font-semibold text-ink">{tile.value}</div>
        </div>
      ))}
      {generated}
    </div>
  );
}

function DemandTable({ report }: { report: DemandReport }) {
  return (
    <Table
      rows={report.data.length}
      empty="No order in this window, so there is nothing to report. Widen the dates above to look further back."
      head={
        <tr>
          <Th>Day</Th>
          <Th>Day part</Th>
          <Th>Channel</Th>
          <Th>Format</Th>
          <Th numeric>Orders</Th>
          <Th numeric>Energy sold</Th>
          <Th numeric>Refusals</Th>
          <Th numeric>p</Th>
          <Th numeric>u</Th>
          <Th numeric>s</Th>
          <Th numeric>q</Th>
          <Th numeric>k</Th>
          <Th numeric>E</Th>
          <Th numeric>Overflow P&amp;L</Th>
          <Th numeric>Stake needed</Th>
        </tr>
      }
    >
      {report.data.map((row) => (
        <tr key={`${row.day}-${row.day_part}-${row.channel}`}>
          <Td>{row.day}</Td>
          <Td>{row.day_part}</Td>
          <Td mono>{row.channel}</Td>
          <Td>{row.format}</Td>
          <Td numeric>{count(row.orders)}</Td>
          <Td numeric>{count(row.energy_sold)}</Td>
          <Td numeric>{count(row.refusals)}</Td>
          <Td numeric>{centiSunToSun(row.p_centi_sun)}</Td>
          <Td numeric>{microPercent(row.u_micro)}</Td>
          <Td numeric>{microPercent(row.s_micro)}</Td>
          <Td numeric>{centiSunToSun(row.q_centi_sun)}</Td>
          <Td numeric>
            {row.k_micro === null ? (
              <Blank title="s = 1: every unit came from a provider, so k = s/(1 − s) is unbounded" />
            ) : (
              (row.k_micro / 1_000_000).toFixed(3)
            )}
          </Td>
          <Td numeric>
            {row.e_centi_sun === null ? (
              <Blank title="s = 1: there is no own capacity for E to be the price of" />
            ) : (
              centiSunToSun(row.e_centi_sun)
            )}
          </Td>
          <Td numeric>{trx(row.overflow_pnl_sun)}</Td>
          <Td numeric>
            {row.stake_needed_sun === null ? (
              <Blank title="u = 0: the volume that utilisation supports is unbounded" />
            ) : (
              trx(row.stake_needed_sun, 0)
            )}
          </Td>
        </tr>
      ))}
    </Table>
  );
}

function LadderClassTable({ report }: { report: DemandReport }) {
  const exhausted = report.ladder.own_levels_exhausted;
  return (
    <>
      <Table
        rows={report.ladder.by_class.length}
        empty="No order in this window carried a ladder walk, so there is nothing to split by class."
        head={
          <tr>
            <Th>Day</Th>
            <Th>Day part</Th>
            <Th>Class</Th>
            <Th numeric>Energy</Th>
            <Th numeric>Revenue</Th>
            <Th numeric>Realised price</Th>
            <Th numeric>Floor</Th>
            <Th numeric>Over floor</Th>
            <Th numeric>Cost</Th>
            <Th numeric>Margin</Th>
          </tr>
        }
      >
        {report.ladder.by_class.map((row) => (
          <tr key={`${row.day}-${row.day_part}-${row.level_class}`}>
            <Td>{row.day}</Td>
            <Td>{row.day_part}</Td>
            <Td>{row.level_class}</Td>
            <Td numeric>{count(row.energy)}</Td>
            <Td numeric>{trx(row.revenue_sun)}</Td>
            <Td numeric>{centiSunToSun(row.realised_price_centi_sun)}</Td>
            <Td numeric>
              {row.floor_centi_sun === null ? (
                <Blank title="No floor was recorded for these orders" />
              ) : (
                centiSunToSun(row.floor_centi_sun)
              )}
            </Td>
            <Td numeric>
              {row.price_over_floor_centi_sun === null ? (
                <Blank />
              ) : (
                centiSunToSun(row.price_over_floor_centi_sun)
              )}
            </Td>
            <Td numeric>{trx(row.cost_sun)}</Td>
            <Td numeric>{trx(row.margin_sun)}</Td>
          </tr>
        ))}
      </Table>
      <p class="mt-2 text-[11px] leading-relaxed text-ink-muted">
        Own levels exhausted: <span class="text-ink">{count(exhausted.count)}</span> orders walked
        past own capacity for <span class="text-ink">{count(exhausted.energy)}</span> energy
        {exhausted.first_at === null
          ? "."
          : `, between ${utc(exhausted.first_at)} and ${utc(exhausted.last_at)}.`}{" "}
        {exhausted.day_parts.length === 0
          ? ""
          : `Most often in ${exhausted.day_parts.map((part) => `${part.day_part} (${part.count})`).join(", ")}.`}
      </p>
    </>
  );
}
